import { CheckCircleIcon } from "@heroicons/react/20/solid";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { FunctionComponent } from "react";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

import { getDataFromResponse, handleApiError } from "~/api/axiosUtils.ts";
import Button from "~/components/buttons/Button/Button";
import Card from "~/components/Card/Card";
import { SwitchPlain } from "~/components/formElements/Switch/Switch.tsx";
import { TEAM_UTILISATION_REPORT_PAGE_STATE_KEY } from "~/constants/pageStateStorageKeys.ts";
import { useAggregatedDataV2 } from "~/hooks/useAggregatedDataV2.ts";
import usePageStateStorage from "~/hooks/usePageStateStorage";
import { useAllCareerLevels } from "~/modules/humanResources/api/careerLevel/careerLevelQueries.ts";
import { CareerLevelId } from "~/modules/humanResources/api/careerLevel/careerLevelTypes.ts";
import { useAllStaffMembers } from "~/modules/humanResources/api/staffMember/staffMemberQueries.ts";
import { useAllProjects } from "~/modules/project/api/project/projectQueries.ts";
import {
	recalculateTeamUtilisationReport,
} from "~/modules/reports/api/utilisationReport/utilisationReportApiDispatchers.ts";
import { utilisationReportQueries } from "~/modules/reports/api/utilisationReport/utilisationReportQueries.ts";
import type { TeamUtilisationMonth } from "~/modules/reports/api/utilisationReport/utilisationReportTypes.ts";
import DownloadTeamUtilisationReportExcelButton
	from "~/modules/reports/components/GlobalReportsView/components/TeamUtilisationReportSection/components/DownloadTeamUtilisationReportExcelButton";
import TeamUtilisationTable
	from "~/modules/reports/components/GlobalReportsView/components/TeamUtilisationReportSection/components/TeamUtilisationTable";
import type {
	ResolvedGroup,
} from "~/modules/reports/components/GlobalReportsView/components/TeamUtilisationReportSection/components/TeamUtilisationTable.tsx";
import UtilisationReportRefreshControl
	from "~/modules/reports/components/GlobalReportsView/components/UtilisationReportSection/components/UtilisationReportRefreshControl";
import { ReportsHeaderSlotContext } from "~/modules/reports/components/GlobalReportsView/reportsHeaderSlotContext.ts";
import LoadingPage from "~/pages/LoadingPage.tsx";
import { byObjectProperty } from "~/utils/sortFunctions.ts";

type Props = {
	year: string;
};

type TeamUtilisationReportPageState = {
	showSalesAndInternal: boolean;
	showDaysTracked: boolean;
	showSelection: boolean;
	collapsedGroupIds: string[];
	collapsedMemberIds: string[];
	selectedUserIds: string[];
};

const defaultPageState: TeamUtilisationReportPageState = {
	showSalesAndInternal: false,
	showDaysTracked: true,
	showSelection: false,
	collapsedGroupIds: [],
	collapsedMemberIds: [],
	selectedUserIds: [],
};

const CAREER_LEVEL_GROUP_ORDER: CareerLevelId[] = [
	CareerLevelId.Partner,
	CareerLevelId.SeniorManager,
	CareerLevelId.Manager,
	CareerLevelId.SeniorConsultant,
	CareerLevelId.Consultant,
	CareerLevelId.Analyst,
	CareerLevelId.IT,
	CareerLevelId.Student,
	CareerLevelId.ManagingDirector,
];

const groupOrderIndex = (careerLevelId: string) => {
	const index = CAREER_LEVEL_GROUP_ORDER.indexOf(careerLevelId as CareerLevelId);
	return index === -1 ? CAREER_LEVEL_GROUP_ORDER.length : index;
};

const TeamUtilisationReportSection: FunctionComponent<Props> = ({ year }) => {
	const { pageState, setPageState } = usePageStateStorage<TeamUtilisationReportPageState>({
		pageKey: TEAM_UTILISATION_REPORT_PAGE_STATE_KEY,
		defaultState: defaultPageState,
	});

	const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
	const [showSalesAndInternal, setShowSalesAndInternal] = useState<boolean>(pageState.showSalesAndInternal);
	const [showDaysTracked, setShowDaysTracked] = useState<boolean>(pageState.showDaysTracked);
	const [showSelection, setShowSelection] = useState<boolean>(pageState.showSelection);
	const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set(pageState.collapsedGroupIds));
	const [collapsedMembers, setCollapsedMembers] = useState<Set<string>>(new Set(pageState.collapsedMemberIds));
	const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set(pageState.selectedUserIds));
	const queryClient = useQueryClient();
	const headerSlot = useContext(ReportsHeaderSlotContext);

	// Persist the team report controls, so a reload restores them. Sets become id arrays.
	useEffect(() => {
		setPageState({
			showSalesAndInternal,
			showDaysTracked,
			showSelection,
			collapsedGroupIds: [...collapsedGroups],
			collapsedMemberIds: [...collapsedMembers],
			selectedUserIds: [...selectedUserIds],
		});
	}, [showSalesAndInternal, showDaysTracked, showSelection, collapsedGroups, collapsedMembers, selectedUserIds, setPageState]);

	const { data, options } = useAggregatedDataV2({
		careerLevels: useAllCareerLevels(),
		staffMembers: useAllStaffMembers(),
		projects: useAllProjects(),
		report: useQuery(utilisationReportQueries.team(year || "")),
	});

	const { careerLevels, staffMembers, projects, report } = data;
	const { isLoading, loadingPercent } = options;

	// The table always shows all 12 months of the year, even when the report holds
	// fewer. A month without a cell renders "--".
	const displayMonths = useMemo<TeamUtilisationMonth[]>(
		() => Array.from({ length: 12 }, (_, index) => ({ year: Number(year), month: index + 1 })),
		[year],
	);

	const handleRefresh = useCallback(async () => {
		setIsRefreshing(true);
		try {
			const freshReport = getDataFromResponse(await recalculateTeamUtilisationReport(year));
			queryClient.setQueryData(utilisationReportQueries.team(year).queryKey, freshReport);
		} catch (error) {
			handleApiError(error);
		} finally {
			setIsRefreshing(false);
		}
	}, [queryClient, year]);

	const resolvedGroups = useMemo<ResolvedGroup[]>(() => {
		if (!report || !staffMembers || !careerLevels || !projects) return [];

		const careerLevelNameById = new Map(careerLevels.map((careerLevel) => [careerLevel.id, careerLevel.displayName]));
		const staffNameById = new Map(staffMembers.map((staffMember) => [staffMember.id, staffMember.fullName]));
		const projectTitleById = new Map(projects.map((project) => [project.id, project.title]));

		const groups: ResolvedGroup[] = report.careerLevelGroups.filter(group => group.careerLevelId !== CareerLevelId.Assistant).map((group) => {
			const members = group.members.map((member) => {
				const projectRows = member.rows
					.filter((row) => row.type === "project")
					.map((row) => ({ ...row, label: projectTitleById.get(row.projectId ?? "") ?? "Projekt" }))
					.sort(byObjectProperty("label"));

				return {
					currentFtePercentage: member.currentFtePercentage,
					staffMemberId: member.staffMemberId,
					userId: member.userId,
					name: staffNameById.get(member.staffMemberId) ?? "Unbekannt",
					mainRow: member.rows.find((row) => row.type === "main"),
					projectRows,
					internalRow: member.rows.find((row) => row.type === "internal"),
					salesRow: member.rows.find((row) => row.type === "sales"),
				};
			}).sort(byObjectProperty("name"));

			return {
				careerLevelId: group.careerLevelId,
				displayName: careerLevelNameById.get(group.careerLevelId) ?? "",
				members,
			};
		});

		return groups.sort((a, b) => groupOrderIndex(a.careerLevelId) - groupOrderIndex(b.careerLevelId));
	}, [report, careerLevels, staffMembers, projects]);

	// A stored selection may hold user ids that this year's report does not contain.
	// Those ids are ignored: the selection counts as visible only when a current member
	// matches it. This drives the toggle's disabled state and the fall-back below.
	const hasVisibleSelection = useMemo(
		() => resolvedGroups.some((group) => group.members.some((member) => selectedUserIds.has(member.userId))),
		[resolvedGroups, selectedUserIds],
	);

	// In the selection view, keep only groups that hold a selected member.
	const displayedGroups = useMemo<ResolvedGroup[]>(() => {
		if (!showSelection) return resolvedGroups;

		return resolvedGroups
			.map((group) => ({
				...group,
				members: group.members.filter((member) => selectedUserIds.has(member.userId)),
			}))
			.filter((group) => group.members.length > 0);
	}, [resolvedGroups, showSelection, selectedUserIds]);

	// The selection view needs at least one visible selected user. When the last one is
	// removed (or none of the stored ids match this year), return to the normal view.
	useEffect(() => {
		if (showSelection && !hasVisibleSelection) setShowSelection(false);
	}, [showSelection, hasVisibleSelection]);

	const toggleGroup = useCallback((careerLevelId: string) => {
		setCollapsedGroups((previous) => {
			const next = new Set(previous);
			next.has(careerLevelId) ? next.delete(careerLevelId) : next.add(careerLevelId);
			return next;
		});
	}, []);

	const toggleMember = useCallback((staffMemberId: string) => {
		setCollapsedMembers((previous) => {
			const next = new Set(previous);
			next.has(staffMemberId) ? next.delete(staffMemberId) : next.add(staffMemberId);
			return next;
		});
	}, []);

	const allGroupIds = useMemo(() => resolvedGroups.map((group) => group.careerLevelId), [resolvedGroups]);
	const allMemberIds = useMemo(
		() => resolvedGroups.flatMap((group) => group.members.map((member) => member.staffMemberId)),
		[resolvedGroups],
	);

	// "All collapsed" and "all expanded" look only at the ids this year holds, so a stored
	// id from another year does not skew the result.
	const allGroupsCollapsed = allGroupIds.length > 0 && allGroupIds.every((id) => collapsedGroups.has(id));
	const allGroupsExpanded = allGroupIds.length > 0 && allGroupIds.every((id) => !collapsedGroups.has(id));
	const allMembersCollapsed = allMemberIds.length > 0 && allMemberIds.every((id) => collapsedMembers.has(id));
	const allMembersExpanded = allMemberIds.length > 0 && allMemberIds.every((id) => !collapsedMembers.has(id));

	// Each toggle keeps its mode until every item reaches one extreme. It starts in
	// "collapse" mode ("einklappen"), so a mixed initial state shows "einklappen".
	const [groupsToggleMode, setGroupsToggleMode] = useState<"expand" | "collapse">("collapse");
	const [membersToggleMode, setMembersToggleMode] = useState<"expand" | "collapse">("collapse");

	useEffect(() => {
		if (allGroupsCollapsed) setGroupsToggleMode("expand");
		else if (allGroupsExpanded) setGroupsToggleMode("collapse");
	}, [allGroupsCollapsed, allGroupsExpanded]);

	useEffect(() => {
		if (allMembersCollapsed) setMembersToggleMode("expand");
		else if (allMembersExpanded) setMembersToggleMode("collapse");
	}, [allMembersCollapsed, allMembersExpanded]);

	const handleToggleAllGroups = useCallback(() => {
		setCollapsedGroups(groupsToggleMode === "expand" ? new Set() : new Set(allGroupIds));
	}, [groupsToggleMode, allGroupIds]);

	const handleToggleAllMembers = useCallback(() => {
		setCollapsedMembers(membersToggleMode === "expand" ? new Set() : new Set(allMemberIds));
	}, [membersToggleMode, allMemberIds]);

	const addUser = useCallback((userId: string) => {
		setSelectedUserIds((previous) => new Set(previous).add(userId));
	}, []);

	const removeUser = useCallback((userId: string) => {
		setSelectedUserIds((previous) => {
			const next = new Set(previous);
			next.delete(userId);
			return next;
		});
	}, []);

	const toggleShowSelection = useCallback(() => setShowSelection((previous) => !previous), []);

	return <div className="grid size-full grid-rows-[auto_1fr] gap-y-2 overflow-hidden pb-8 pt-4">
		{isLoading && <LoadingPage pcent={loadingPercent} />}
		{!isLoading && report && <>
			{headerSlot && createPortal(<UtilisationReportRefreshControl generatedAt={report.generatedAt}
																		 isRefreshing={isRefreshing}
																		 onRefresh={handleRefresh} />, headerSlot)}
			<Card>
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-x-6">
						<SwitchPlain checked={showSalesAndInternal}
									 name="showSalesAndInternal"
									 label="Sales & Intern inkludieren"
									 inputSize="sm"
									 onChange={setShowSalesAndInternal} />
						<SwitchPlain checked={showDaysTracked}
									 name="showDaysTracked"
									 inputSize="sm"
									 label="Gebuchte Tage anzeigen"
									 onChange={setShowDaysTracked} />
						<div className="flex items-center gap-x-2">
							<Button theme="primary"
									size="sm"
									onClick={handleToggleAllGroups}>
								{groupsToggleMode === "expand" ? "Alle Kategorien öffnen" : "Alle Kategorien schließen"}
							</Button>
							<Button theme="primary"
									size="sm"
									onClick={handleToggleAllMembers}>
								{membersToggleMode === "expand" ? "Projekte anzeigen" : "Projekte verbergen"}
							</Button>
						</div>
					</div>
					<div className="flex items-center gap-x-3">
						<Button theme="secondary"
								size="sm"
								onClick={toggleShowSelection}
								disabled={!hasVisibleSelection}>
							<CheckCircleIcon className="size-4" />
							{showSelection ? "Auswahl ausblenden" : "Auswahl anzeigen"}
						</Button>
						<DownloadTeamUtilisationReportExcelButton groups={resolvedGroups}
																  months={displayMonths}
																  year={year} />
					</div>
				</div>
			</Card>
			<TeamUtilisationTable groups={displayedGroups}
								  months={displayMonths}
								  collapsedGroups={collapsedGroups}
								  collapsedMembers={collapsedMembers}
								  selectedUserIds={selectedUserIds}
								  showDaysTracked={showDaysTracked}
								  showSalesAndInternal={showSalesAndInternal}
								  showSelection={showSelection}
								  onToggleGroup={toggleGroup}
								  onToggleMember={toggleMember}
								  onAddUser={addUser}
								  onRemoveUser={removeUser} />
		</>}
	</div>;
};

export default TeamUtilisationReportSection;
